import React, { useEffect } from "react";
import Head from "next/head";
import globalBusinessMetadata from "@foundation/global-business-metadata";
import {
  Favicon,
  getOpenGraphTags,
  getStaticMetadataTags,
  getTwitterMetadataTags,
} from "@foundation/metadata";

import type { PageMetadata, QuoteMetadata } from "~types/types";
import { getContentMetadata } from "~utils/adUtils";
import { getOrganizationJsonLd } from "~utils/seo/buildOrganizationJsonLd";
import { getPageJsonLd } from "~utils/seo/buildWebPageJsonLd";

const FULL_PAGE_REFRESH_SECONDS = "3600";

export interface MetaTagsProps {
  metadata: QuoteMetadata | PageMetadata;
}

const COLOR_ENV = process.env.NEXT_PUBLIC_COLOR_ENV;

export default function MetaTags(props: MetaTagsProps) {
  const { constructedTitle, description, keywords, title, url } =
    props.metadata;

  const staticMetadataTags = getStaticMetadataTags({
    canonical: url,
    description,
    keywords,
  });

  const twitterTags = getTwitterMetadataTags({
    twitterHandle: "@markets",
    creator: "@markets",
    description,
    title,
    image: "https://www.bloomberg.com/favicon-black.png",
  });

  const openGraphTags = getOpenGraphTags({
    type: "company",
    title,
    url,
    image: "https://www.bloomberg.com/favicon-black.png",
    description,
    keywords,
  });

  const { mediaSecurityType, tickerId } = props.metadata as QuoteMetadata;

  const webPageJsonLd = JSON.stringify(
    getPageJsonLd(props.metadata as QuoteMetadata)
  );

  const organizationJsonLd =
    tickerId &&
    JSON.stringify(getOrganizationJsonLd(props.metadata as QuoteMetadata));

  const contentMetadata =
    tickerId && getContentMetadata(tickerId, mediaSecurityType);

  useEffect(() => {
    if (window?.__bloomberg__?.setContentMetadata) {
      window?.__bloomberg__?.setContentMetadata(contentMetadata);
    }
  }, []);

  return (
    <>
      <Head>
        <title>{constructedTitle || title}</title>
        <Favicon env={COLOR_ENV} />
        <meta httpEquiv="refresh" content={FULL_PAGE_REFRESH_SECONDS} />
        {staticMetadataTags}
        {twitterTags}
        {openGraphTags}
        {/* This is needed to signal to AbbaClient to get content metadata for this page */}
        <meta name="will-have-content-metadata" content="true" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(globalBusinessMetadata()),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: webPageJsonLd }}
        />
        {organizationJsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: organizationJsonLd }}
          />
        )}
      </Head>
    </>
  );
}
